// The runtime door, not the `@/features/performanceTracking` barrel: the barrel
// also exports the abandonment reporter, whose debug-log atom is created at
// import time. `NavItem` is rendered by dozens of suites that stub `jotai`
// partially, and none of them should have to know about that reporter.
import {
  appendCheckpoint,
  cancelTracking,
  startTracking,
  TrackingKey,
} from '@heidi/runtime/performance';
import { type NextRouter, useRouter } from 'next/router';
import { useEffect } from 'react';
import { routePath, toRouteLabel } from './nav-item.utils';

/**
 * `perf.app.nav_menu_switch` (ARD-1955): a left-sidebar click timed from user
 * intent to the destination page's shell being painted, one frame after commit.
 * It does not wait for the page's data.
 *
 * - START: {@link startNavMenuSwitchTracking}, from `NavItem`'s internal-link
 *   `onClick` after the row's own handler has run without `preventDefault`.
 * - CHECKPOINTS: {@link useNavMenuSwitchRouterCheckpoints}, mounted once in
 *   `SideNav`, from the Next router events.
 * - FEEDBACK: NavigationLoadingView records `feedback_paint_opportunity` after
 *   two frames, separately from workspace arrival (no clinical metadata).
 * - FINISH: the `PerfTrackingStopper` beside `{children}` in `ScribeLayout`,
 *   which every sidebar destination renders.
 *
 * One key for the whole menu; the destination rides in metadata as `nav_item`,
 * the same way `SCRIBE_TAB_SWITCH` carries `tab_name`. Platform, app version,
 * browser and OS are NOT in the metadata — the RUM SDK attaches `service`,
 * `version`, `@browser.*`, `@os.*` and `installer_type` to every action.
 */
const KEY = TrackingKey.APP_NAV_MENU_SWITCH;

export const NAV_MENU_SWITCH_CHECKPOINTS = {
  ROUTE_CHANGE_START: 'route_change_start',
  ROUTE_CHANGE_COMPLETE: 'route_change_complete',
} as const;

/**
 * Opens the entry for a sidebar click. Compared on the query-stripped path so a
 * same-page click with a different `?tab=` never opens an entry that no route
 * change will close. `from_route` is the Next route PATTERN (`router.pathname`),
 * so no session or patient id reaches Datadog; `to_route` collapses the session
 * routes for the same reason.
 */
export const startNavMenuSwitchTracking = ({
  href,
  router,
  testId,
}: {
  href: string;
  router: Pick<NextRouter, 'asPath' | 'pathname'>;
  testId?: string;
}): void => {
  if (routePath(href) === routePath(router.asPath)) return;
  startTracking({
    key: KEY,
    metadata: {
      nav_item: testId ?? 'unknown',
      from_route: router.pathname,
      to_route: toRouteLabel(href),
    },
  });
};

/** Close an in-flight sidebar nav span that will never complete (e.g. discard-guard abort). */
export const cancelNavMenuSwitchTracking = (): void => {
  cancelTracking({ key: KEY });
};

/**
 * Next emits `routeChangeError` with `cancelled: true` when a newer navigation
 * aborts the one in flight. The shape is Next's, not ours, so read it
 * defensively.
 */
const isCancelledRouteChange = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  (err as { cancelled?: boolean }).cancelled === true;

/**
 * Adds the router phases to an in-flight entry. Every call is a no-op when no
 * entry is active, so navigations that did not start from the sidebar are
 * harmless.
 *
 * - `routeChangeStart` → `route_change_start` (click → router picked it up:
 *   the main-thread cost of the click handler and React transition)
 * - `routeChangeComplete` → `route_change_complete` (chunk + data loaded)
 * - `routeChangeError` with `cancelled: false` → cancel; a failed navigation
 *   is not performance data.
 * - `routeChangeError` with `cancelled: true` → leave it alone. That error
 *   belongs to the OLDER navigation a rapid second click aborted, and it fires
 *   AFTER the second click has already replaced the entry (same key
 *   overwrites). Cancelling here would kill the entry that is still live.
 */
export function useNavMenuSwitchRouterCheckpoints(): void {
  const router = useRouter();

  useEffect(() => {
    const onStart = () =>
      appendCheckpoint({
        key: KEY,
        checkpointName: NAV_MENU_SWITCH_CHECKPOINTS.ROUTE_CHANGE_START,
      });
    const onComplete = () =>
      appendCheckpoint({
        key: KEY,
        checkpointName: NAV_MENU_SWITCH_CHECKPOINTS.ROUTE_CHANGE_COMPLETE,
      });
    const onError = (err: unknown) => {
      if (isCancelledRouteChange(err)) return;
      cancelTracking({ key: KEY });
    };

    router.events.on('routeChangeStart', onStart);
    router.events.on('routeChangeComplete', onComplete);
    router.events.on('routeChangeError', onError);
    return () => {
      router.events.off('routeChangeStart', onStart);
      router.events.off('routeChangeComplete', onComplete);
      router.events.off('routeChangeError', onError);
    };
  }, [router.events]);
}
